import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { useLessons, type Lesson } from '@/hooks/useLessons'
import { useDrivingSkills, useLessonAssessments, useStudentSkillProgress, type SkillOutcome, type DrivingSkill } from '@/hooks/useLessonAssessments'
import { usePracticeAssignments, type AssignmentStatus } from '@/hooks/usePracticeAssignments'
import { CurriculumAssignmentPanel } from '@/components/curriculum/CurriculumAssignmentPanel'
import { ProgressSheetPanel } from '@/components/curriculum/ProgressSheetPanel'
import { GaugeProgress } from '@/components/ui/GaugeProgress'

interface StudentData {
  // From profiles
  id: string
  fullName: string | null
  email: string
  phone: string | null
  // From student_profiles
  dateOfBirth: string | null
  g1LicenceNumber: string | null
  g1IssueDate: string | null
  g1ExpiryDate: string | null
  sex: 'male' | 'female' | null
  guardianEmail: string | null
  guardianPhone: string | null
  // From instructor_student_relationships
  relationshipId: string
  startedAt: string
  schoolId: string | null
  status: 'active' | 'ended' | 'historical'
  endedAt: string | null
}

function ageInYears(dob: string | null): number | null {
  if (!dob) return null
  const birth = new Date(dob)
  const now = new Date()
  let age = now.getFullYear() - birth.getFullYear()
  const beforeBirthdayThisYear =
    now.getMonth() < birth.getMonth() ||
    (now.getMonth() === birth.getMonth() && now.getDate() < birth.getDate())
  if (beforeBirthdayThisYear) age -= 1
  return age
}

function formatDate(iso: string | null) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-CA', { month: 'long', day: 'numeric', year: 'numeric' })
}

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString('en-CA', {
    month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  })
}

// <input type="datetime-local"> wants "YYYY-MM-DDTHH:mm" in LOCAL time (no timezone suffix);
// toISOString() would shift to UTC and show the wrong wall-clock time to the instructor.
function toDatetimeLocalValue(d: Date) {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

const STATUS_COLOR: Record<Lesson['status'], string> = {
  scheduled: '#0F7C6C',
  completed: '#1B2A4A',
  cancelled: '#C0522A',
}

const OUTCOME_LABEL: Record<SkillOutcome, string> = {
  demonstrated: 'Demonstrated',
  needs_practice: 'Needs practice',
  not_assessed: 'Not assessed',
  taught: 'Taught',
}

const OUTCOME_COLOR: Record<SkillOutcome, string> = {
  demonstrated: '#0F7C6C',
  needs_practice: '#9A6C0E',
  not_assessed: 'rgba(27,42,74,0.35)',
  taught: '#2B5FA3',
}

function formatDateShort(iso: string) {
  return new Date(iso).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' })
}

function g1ExpiryStatus(expiryIso: string | null): { text: string; color: string } {
  if (!expiryIso) return { text: '—', color: 'rgba(27,42,74,0.45)' }
  const expiry = new Date(expiryIso)
  const now    = new Date()
  const days   = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
  if (days < 0)   return { text: `Expired ${formatDate(expiryIso)}`, color: '#C0522A' }
  if (days <= 90) return { text: `${formatDate(expiryIso)} (${days} days)`, color: '#9A6C0E' }
  return { text: formatDate(expiryIso), color: '#0F7C6C' }
}

function Field({ label, value, valueColor }: { label: string; value: string; valueColor?: string }) {
  return (
    <div>
      <div style={{ font: '500 11.5px Inter, sans-serif', color: 'rgba(27,42,74,0.45)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ font: '400 14px Inter, sans-serif', color: valueColor ?? '#1B2A4A' }}>
        {value || '—'}
      </div>
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ background: '#fff', border: '1px solid rgba(27,42,74,0.08)', borderRadius: 14, padding: '22px 22px', marginBottom: 16 }}>
      <div style={{ font: 'var(--text-section)', color: 'var(--ink)', marginBottom: 18 }}>{title}</div>
      {children}
    </div>
  )
}

function LessonAssessmentPanel({ lessonId, readOnly }: { lessonId: string; readOnly: boolean }) {
  const { skills, loading: skillsLoading, error: skillsError } = useDrivingSkills()
  const { assessments, loading: assessmentsLoading, savingSkillId, saveAssessment } = useLessonAssessments(lessonId)
  const [observationDrafts, setObservationDrafts] = useState<Record<string, string>>({})
  const [saveError, setSaveError] = useState<string | null>(null)

  const assessmentBySkill = new Map(assessments.map(a => [a.skillId, a]))

  const SAVE_ERROR_MESSAGES: Record<string, string> = {
    lesson_not_found: 'This lesson could not be found.',
    unauthorized: 'You are not authorized to assess this lesson.',
    skill_not_found: 'That skill could not be found.',
    observation_too_long: 'Observation is too long (2000 characters max).',
    relationship_not_active: 'This student relationship is no longer active, so this assessment can no longer be changed.',
    internal_error: 'Something went wrong. Please try again.',
  }

  async function handleSetOutcome(skillId: string, outcome: SkillOutcome) {
    setSaveError(null)
    const observation = observationDrafts[skillId] ?? assessmentBySkill.get(skillId)?.observation ?? null
    const result = await saveAssessment(skillId, outcome, observation?.trim() || null)
    if (!result.ok) setSaveError(SAVE_ERROR_MESSAGES[result.code ?? ''] ?? 'Could not save the assessment.')
  }

  async function handleSaveObservation(skillId: string) {
    const current = assessmentBySkill.get(skillId)
    const outcome = current?.outcome ?? 'not_assessed'
    setSaveError(null)
    const result = await saveAssessment(skillId, outcome, (observationDrafts[skillId] ?? '').trim() || null)
    if (!result.ok) setSaveError(SAVE_ERROR_MESSAGES[result.code ?? ''] ?? 'Could not save the observation.')
  }

  if (skillsLoading || assessmentsLoading) {
    return <div style={{ font: '400 13px Inter, sans-serif', color: 'rgba(27,42,74,0.4)', padding: '12px 0' }}>Loading skills…</div>
  }
  if (skillsError) {
    return <div className='banner-error'>{skillsError}</div>
  }

  return (
    <div style={{ padding: '14px 0 2px' }}>
      {saveError && <div className='banner-error' style={{ marginBottom: 10 }}>{saveError}</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {skills.map(skill => {
          const current = assessmentBySkill.get(skill.id)
          const outcome = current?.outcome ?? 'not_assessed'
          const isSaving = savingSkillId === skill.id
          const draft = observationDrafts[skill.id] ?? current?.observation ?? ''
          return (
            <div key={skill.id} style={{ borderBottom: '1px solid rgba(27,42,74,0.06)', paddingBottom: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                <div style={{ font: '500 13.5px Inter, sans-serif', color: '#1B2A4A' }}>{skill.name}</div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {(['demonstrated', 'needs_practice', 'not_assessed'] as SkillOutcome[]).map(opt => (
                    <button
                      key={opt}
                      onClick={() => handleSetOutcome(skill.id, opt)}
                      disabled={isSaving || readOnly}
                      style={{
                        border: `1px solid ${outcome === opt ? OUTCOME_COLOR[opt] : 'rgba(27,42,74,0.15)'}`,
                        background: outcome === opt ? OUTCOME_COLOR[opt] : 'none',
                        color: outcome === opt ? '#fff' : 'rgba(27,42,74,0.6)',
                        borderRadius: 7, padding: '5px 10px', font: '500 11.5px Inter, sans-serif',
                        cursor: (isSaving || readOnly) ? 'default' : 'pointer', opacity: isSaving ? 0.6 : 1,
                      }}
                    >
                      {OUTCOME_LABEL[opt]}
                    </button>
                  ))}
                </div>
              </div>
              <textarea
                placeholder='Observation (optional)'
                value={draft}
                onChange={e => setObservationDrafts(d => ({ ...d, [skill.id]: e.target.value }))}
                onBlur={() => { if (!readOnly && draft !== (current?.observation ?? '')) handleSaveObservation(skill.id) }}
                readOnly={readOnly}
                rows={1}
                style={{
                  width: '100%', marginTop: 6, padding: '6px 9px', borderRadius: 6,
                  border: '1px solid rgba(27,42,74,0.1)', font: '400 12.5px Inter, sans-serif', resize: 'vertical',
                  background: readOnly ? 'rgba(27,42,74,0.03)' : '#fff',
                }}
              />
            </div>
          )
        })}
      </div>
    </div>
  )
}

function SkillProgress({
  studentId, readOnly, onAssignPractice,
}: {
  studentId: string
  readOnly: boolean
  onAssignPractice: (skillId: string, skillName: string) => void
}) {
  const { entries, loading, error } = useStudentSkillProgress(studentId)

  if (loading) return <div style={{ font: '400 13px Inter, sans-serif', color: 'rgba(27,42,74,0.4)' }}>Loading skill progress…</div>
  if (error) return <div className='banner-error'>{error}</div>
  if (entries.length === 0) return <ComingSoon label='Skill progress' />

  const needsPractice = entries.filter(e => e.latestOutcome === 'needs_practice')
  const demonstrated = entries.filter(e => e.latestOutcome === 'demonstrated')
  const sorted = [...entries].sort((a, b) => a.skillName.localeCompare(b.skillName))

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
        <GaugeProgress value={demonstrated.length} max={entries.length} size={64} />
        <div>
          <div style={{ font: '600 13.5px Inter, sans-serif', color: 'var(--ink)' }}>
            {demonstrated.length} of {entries.length} skills demonstrated
          </div>
          {needsPractice.length > 0 && (
            <div style={{ font: '500 12.5px Inter, sans-serif', color: '#9A6C0E', marginTop: 2 }}>
              {needsPractice.length} skill{needsPractice.length === 1 ? '' : 's'} currently flagged as needing practice
            </div>
          )}
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {sorted.map(entry => {
          const lessonDate = entry.latestLessonScheduledAt ?? entry.latestUpdatedAt
          return (
            <div key={entry.skillId} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              borderBottom: '1px solid rgba(27,42,74,0.06)', paddingBottom: 8, gap: 10, flexWrap: 'wrap',
            }}>
              <div>
                <div style={{ font: '500 13.5px Inter, sans-serif', color: '#1B2A4A' }}>
                  {entry.skillName}
                  {entry.skillCategory && (
                    <span style={{ marginLeft: 8, font: '500 11px Inter, sans-serif', color: 'rgba(27,42,74,0.4)' }}>
                      {entry.skillCategory}
                    </span>
                  )}
                </div>
                <div style={{ font: '400 11.5px Inter, sans-serif', color: 'rgba(27,42,74,0.4)' }}>
                  Assessed in lesson on {formatDateShort(lessonDate)} · {entry.timesAssessed} assessment{entry.timesAssessed === 1 ? '' : 's'}
                </div>
                {entry.previousOutcome && (
                  <div style={{ font: '400 11.5px Inter, sans-serif', color: 'rgba(27,42,74,0.4)', marginTop: 2 }}>
                    Previously: <span style={{ color: OUTCOME_COLOR[entry.previousOutcome] }}>{OUTCOME_LABEL[entry.previousOutcome]}</span>
                    {entry.previousUpdatedAt && ` (${formatDateShort(entry.previousUpdatedAt)})`}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                <span style={{
                  font: '600 11.5px Inter, sans-serif', color: OUTCOME_COLOR[entry.latestOutcome],
                  textTransform: 'uppercase', letterSpacing: '0.5px',
                }}>
                  {OUTCOME_LABEL[entry.latestOutcome]}
                </span>
                {!readOnly && entry.latestOutcome === 'needs_practice' && (
                  <button
                    onClick={() => onAssignPractice(entry.skillId, entry.skillName)}
                    style={{ background: 'rgba(27,42,74,0.06)', border: 'none', borderRadius: 7, padding: '5px 10px', font: '500 11.5px Inter, sans-serif', color: '#1B2A4A', cursor: 'pointer' }}
                  >
                    Assign practice
                  </button>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

interface PackageUsage {
  hasPackage: boolean
  packageName?: string
  hoursTotal?: number
  hoursUsed?: number
  hoursRemaining?: number
}

// Package usage summary (migration 092) — shown to the instructor while logging/tracking
// lessons so they can see, at a glance, how many of the student's purchased in-car hours
// are left. Purely informational: per the product owner's own choice, exhausting a
// package warns but never blocks logging a lesson.
function PackageUsagePanel({ relationshipId }: { relationshipId: string }) {
  const [usage, setUsage] = useState<PackageUsage | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      const { data } = await supabase.rpc('get_relationship_package_usage', { p_relationship_id: relationshipId })
      if (cancelled) return
      const result = data as { ok: boolean; has_package?: boolean; package_name?: string; hours_total?: number; hours_used?: number; hours_remaining?: number } | null
      if (result?.ok && result.has_package) {
        setUsage({
          hasPackage: true,
          packageName: result.package_name,
          hoursTotal: result.hours_total,
          hoursUsed: result.hours_used,
          hoursRemaining: result.hours_remaining,
        })
      } else {
        setUsage({ hasPackage: false })
      }
      setLoading(false)
    }
    load()
    return () => { cancelled = true }
  }, [relationshipId])

  if (loading || !usage || !usage.hasPackage) return null

  const exhausted = (usage.hoursRemaining ?? 0) <= 0

  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap',
      background: exhausted ? 'var(--danger-10)' : 'rgba(27,42,74,0.03)',
      border: `1px solid ${exhausted ? 'var(--danger-25)' : 'rgba(27,42,74,0.08)'}`,
      borderRadius: 12, padding: '14px 18px', marginBottom: 16,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <GaugeProgress
          value={usage.hoursUsed ?? 0} max={usage.hoursTotal ?? 1} size={52}
          color={exhausted ? 'var(--danger)' : '#0F7C6C'}
        />
        <div>
          <div style={{ font: '600 14px Inter, sans-serif', color: '#1B2A4A' }}>{usage.packageName}</div>
          <div style={{ font: '400 12.5px Inter, sans-serif', color: 'rgba(27,42,74,0.55)', marginTop: 2 }}>
            {usage.hoursUsed} of {usage.hoursTotal} in-car hours used
          </div>
        </div>
      </div>
      <div style={{
        font: '700 15px Inter, sans-serif',
        color: exhausted ? 'var(--danger)' : '#0F7C6C',
      }}>
        {exhausted ? 'Package exhausted' : `${usage.hoursRemaining}h remaining`}
      </div>
    </div>
  )
}

function useElapsedTime(sinceIso: string | null): string {
  const [, setTick] = useState(0)
  useEffect(() => {
    if (!sinceIso) return
    const id = setInterval(() => setTick(t => t + 1), 1000)
    return () => clearInterval(id)
  }, [sinceIso])
  if (!sinceIso) return ''
  const seconds = Math.max(0, Math.floor((Date.now() - new Date(sinceIso).getTime()) / 1000))
  const mm = Math.floor(seconds / 60).toString().padStart(2, '0')
  const ss = (seconds % 60).toString().padStart(2, '0')
  return `${mm}:${ss}`
}

// Lesson Logging & Tracking Sheet, combined: Start/End is now the primary way a lesson
// gets recorded — click Start, a row appears immediately (date/start time), click End and
// it's done, duration derived server-side, counted as one lesson. A manual "+ Log a past
// lesson" form (the old two-time-entry create_lesson() path) stays available underneath
// as a secondary, explicit action for back-filling a lesson that was taught but never
// started live, never the default.
function LessonLoggingPanel({
  lessons, loading, error, saving, createLesson, startLesson, endLesson, refresh, readOnly,
}: ReturnType<typeof useLessons> & { readOnly: boolean }) {
  const [formOpen, setFormOpen] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [startError, setStartError] = useState<string | null>(null)
  const [scheduledAt, setScheduledAt] = useState(() => toDatetimeLocalValue(new Date()))
  const [finishedAt, setFinishedAt] = useState(() => toDatetimeLocalValue(new Date(Date.now() + 60 * 60 * 1000)))
  const [notes, setNotes] = useState('')
  const [expandedLessonId, setExpandedLessonId] = useState<string | null>(null)
  const [recoveryOpen, setRecoveryOpen] = useState(false)
  const [recoveryFinishedAt, setRecoveryFinishedAt] = useState('')

  // A row with finished_at === null is only a LIVE lesson if its start time has actually
  // arrived — a future Calendar booking (migration 094) also has finished_at === null long
  // before it happens, and must not be shown as if a timer were running. A cancelled
  // booking (migration 094's cancel_booked_lesson()) also has finished_at === null forever
  // -- it was never driven, so it never gets a finish time -- and must be excluded by
  // status, not just scheduled_at, or it reads as a permanently-running timer whose End
  // button can never succeed (the terminal-status trigger rejects it).
  const inProgress = lessons.find(l => l.status === 'scheduled' && l.finishedAt === null && new Date(l.scheduledAt).getTime() <= Date.now())
  const elapsed = useElapsedTime(inProgress?.scheduledAt ?? null)
  const elapsedMinutes = inProgress ? Math.floor((Date.now() - new Date(inProgress.scheduledAt).getTime()) / 60000) : 0
  // 90 minutes covers the great majority of real single lessons with room to spare; past
  // that, a nudge to close it out is more useful than a silently climbing clock.
  const runningLong = elapsedMinutes > 90

  // end_lesson()'s 8-hour CHECK (migration 056) is enforced against whichever p_finished_at
  // is supplied, not just now() -- by the time the recovery panel opens, now() is ALREADY
  // the value that just failed, so defaulting the picker to it (or leaving it unconstrained)
  // guarantees the exact same invalid_duration rejection unless the instructor happens to
  // manually edit the date back to the original lesson day. Clamp both the default value and
  // the picker's own min/max to the only window the backend can actually accept.
  const recoveryWindow = inProgress ? (() => {
    const startMs = new Date(inProgress.scheduledAt).getTime()
    const maxMs = Math.min(Date.now(), startMs + 8 * 60 * 60 * 1000)
    const defaultMs = Math.min(startMs + 60 * 60 * 1000, maxMs)
    return {
      min: toDatetimeLocalValue(new Date(startMs + 60 * 1000)),
      max: toDatetimeLocalValue(new Date(maxMs)),
      maxIso: new Date(maxMs).toISOString(),
      default: toDatetimeLocalValue(new Date(defaultMs)),
    }
  })() : null

  const CREATE_ERROR_MESSAGES: Record<string, string> = {
    invalid_finish_time: 'Finish time must be after the start time.',
    invalid_duration: 'A lesson cannot be longer than 480 minutes (8 hours).',
    finish_time_in_future: 'The end time you entered is in the future.',
    relationship_not_active: 'This student relationship is no longer active, so a new lesson cannot be created.',
    relationship_not_found: 'The student relationship could not be found.',
    unauthorized: 'You are not authorized to create a lesson for this student.',
    scheduled_at_required: 'A lesson start time is required.',
    lesson_already_in_progress: 'A lesson is already in progress — end it before starting another.',
    internal_error: 'Something went wrong. Please try again.',
  }

  async function handleStart() {
    setStartError(null)
    const result = await startLesson()
    if (!result.ok) setStartError(CREATE_ERROR_MESSAGES[result.code ?? ''] ?? 'Could not start the lesson.')
  }

  async function handleEnd() {
    if (!inProgress) return
    setStartError(null)
    const result = await endLesson(inProgress.id)
    if (!result.ok && result.code === 'invalid_duration') {
      // now() is more than 8 hours past the start — the instructor forgot to click End.
      // Offer to back-fill the real end time instead of leaving the lesson stuck forever.
      // Default inside the only window the backend can accept (see recoveryWindow above) --
      // defaulting to now() here would just reproduce the same rejection on submit.
      if (recoveryWindow) setRecoveryFinishedAt(recoveryWindow.default)
      setRecoveryOpen(true)
      return
    }
    if (!result.ok) setStartError(CREATE_ERROR_MESSAGES[result.code ?? ''] ?? 'Could not end the lesson.')
  }

  async function handleRecoverySubmit() {
    if (!inProgress || !recoveryFinishedAt) return
    setStartError(null)
    const result = await endLesson(inProgress.id, new Date(recoveryFinishedAt).toISOString())
    if (!result.ok) {
      setStartError(CREATE_ERROR_MESSAGES[result.code ?? ''] ?? 'Could not end the lesson.')
      return
    }
    setRecoveryOpen(false)
  }

  async function handleCreate() {
    setFormError(null)
    if (!scheduledAt) { setFormError('A lesson start time is required.'); return }
    if (!finishedAt) { setFormError('A lesson finish time is required.'); return }
    if (new Date(finishedAt).getTime() <= new Date(scheduledAt).getTime()) {
      setFormError('Finish time must be after the start time.')
      return
    }

    const result = await createLesson(new Date(scheduledAt).toISOString(), new Date(finishedAt).toISOString(), notes.trim() || null)
    if (!result.ok) {
      setFormError(CREATE_ERROR_MESSAGES[result.code ?? ''] ?? 'Could not create the lesson.')
      return
    }
    setFormOpen(false)
    setScheduledAt(toDatetimeLocalValue(new Date()))
    setFinishedAt(toDatetimeLocalValue(new Date(Date.now() + 60 * 60 * 1000)))
    setNotes('')
  }

  // Goes through cancel_booked_lesson() (migration 094) rather than a raw status update,
  // even for a lesson that wasn't created via the calendar booking flow -- this is the
  // single place late-cancellation flagging, the other party's notification email, and
  // withdrawing any still-pending reschedule request all happen; it works unmodified on
  // any driving_lessons row regardless of how it was created.
  async function handleCancel(lessonId: string) {
    await supabase.functions.invoke('cancel-lesson', { body: { lessonId } })
    await refresh()
  }

  return (
    <div>
      {!readOnly && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap',
          background: inProgress ? 'rgba(15,124,108,0.06)' : 'rgba(27,42,74,0.03)',
          border: `1px solid ${inProgress ? 'rgba(15,124,108,0.25)' : 'rgba(27,42,74,0.08)'}`,
          borderRadius: 12, padding: '16px 20px', marginBottom: 16,
        }}>
          <div>
            {inProgress ? (
              <>
                <div style={{ font: '700 20px "Courier New", monospace', color: runningLong ? '#C0522A' : '#0F7C6C' }}>{elapsed}</div>
                <div style={{ font: '400 12px Inter, sans-serif', color: 'rgba(27,42,74,0.5)', marginTop: 2 }}>
                  Lesson in progress — started {formatDateTime(inProgress.scheduledAt)}
                </div>
                {runningLong && (
                  <div style={{ font: '600 12px Inter, sans-serif', color: '#C0522A', marginTop: 4 }}>
                    This lesson has been running over 90 minutes — still going?
                  </div>
                )}
              </>
            ) : (
              <div style={{ font: '500 13.5px Inter, sans-serif', color: 'rgba(27,42,74,0.6)' }}>
                No lesson in progress right now.
              </div>
            )}
          </div>
          {inProgress ? (
            <button
              onClick={handleEnd}
              disabled={saving}
              style={{
                background: '#C0522A', color: '#fff', border: 'none', borderRadius: 8,
                padding: '11px 24px', font: '700 14px Inter, sans-serif', cursor: saving ? 'default' : 'pointer', flexShrink: 0,
              }}
            >
              ■ End lesson
            </button>
          ) : (
            <button
              onClick={handleStart}
              disabled={saving}
              style={{
                background: '#0F7C6C', color: '#fff', border: 'none', borderRadius: 8,
                padding: '11px 24px', font: '700 14px Inter, sans-serif', cursor: saving ? 'default' : 'pointer', flexShrink: 0,
              }}
            >
              ▶ Start lesson
            </button>
          )}
        </div>
      )}
      {startError && <div className='banner-error' style={{ marginBottom: 16 }}>{startError}</div>}

      {recoveryOpen && inProgress && (
        <div style={{ background: 'rgba(192,82,42,0.06)', border: '1px solid rgba(192,82,42,0.25)', borderRadius: 11, padding: 16, marginBottom: 16 }}>
          <div style={{ font: '600 13.5px Inter, sans-serif', color: '#1B2A4A', marginBottom: 4 }}>
            This lesson ran longer than 8 hours by the clock
          </div>
          <div style={{ font: '400 12.5px Inter, sans-serif', color: 'rgba(27,42,74,0.6)', marginBottom: 12 }}>
            It was probably left open by mistake. Enter when it actually ended to close it out
            — must be between {formatDateTime(inProgress.scheduledAt)} and{' '}
            {recoveryWindow ? formatDateTime(recoveryWindow.maxIso) : '—'} (8 hours max per lesson).
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div>
              <div style={{ font: '500 11.5px Inter, sans-serif', color: 'rgba(27,42,74,0.45)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>
                Actual end time
              </div>
              <input
                type='datetime-local'
                value={recoveryFinishedAt}
                min={recoveryWindow?.min}
                max={recoveryWindow?.max}
                onChange={e => setRecoveryFinishedAt(e.target.value)}
                style={{ padding: '8px 10px', borderRadius: 7, border: '1px solid rgba(27,42,74,0.15)', font: '400 14px Inter, sans-serif' }}
              />
            </div>
            <button
              onClick={handleRecoverySubmit}
              disabled={saving}
              style={{
                background: '#C0522A', color: '#fff', border: 'none', borderRadius: 8,
                padding: '9px 18px', font: '600 13px Inter, sans-serif', cursor: saving ? 'default' : 'pointer',
              }}
            >
              End lesson at this time
            </button>
            <button
              onClick={() => setRecoveryOpen(false)}
              style={{ background: 'none', border: 'none', font: '500 12.5px Inter, sans-serif', color: 'rgba(27,42,74,0.5)', cursor: 'pointer', padding: '9px 4px' }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {!readOnly && (
        <div style={{ marginBottom: 16 }}>
          <button
            onClick={() => { setFormOpen(v => !v); setFormError(null) }}
            style={{ background: 'none', border: 'none', font: '500 12.5px Inter, sans-serif', color: '#0F7C6C', cursor: 'pointer', padding: 0 }}
          >
            {formOpen ? 'Cancel' : '+ Log a past lesson'}
          </button>
        </div>
      )}

      {!readOnly && formOpen && (
        <div style={{ background: 'rgba(27,42,74,0.03)', border: '1px solid rgba(27,42,74,0.08)', borderRadius: 11, padding: 18, marginBottom: 18 }}>
          <div className='fg2' style={{ marginBottom: 14 }}>
            <div>
              <div style={{ font: '500 11.5px Inter, sans-serif', color: 'rgba(27,42,74,0.45)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>
                Start time
              </div>
              <input
                type='datetime-local'
                value={scheduledAt}
                onChange={e => setScheduledAt(e.target.value)}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 7, border: '1px solid rgba(27,42,74,0.15)', font: '400 14px Inter, sans-serif' }}
              />
            </div>
            <div>
              <div style={{ font: '500 11.5px Inter, sans-serif', color: 'rgba(27,42,74,0.45)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>
                Finish time
              </div>
              <input
                type='datetime-local'
                value={finishedAt}
                onChange={e => setFinishedAt(e.target.value)}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 7, border: '1px solid rgba(27,42,74,0.15)', font: '400 14px Inter, sans-serif' }}
              />
            </div>
          </div>
          <div style={{ marginBottom: 14 }}>
            <div style={{ font: '500 11.5px Inter, sans-serif', color: 'rgba(27,42,74,0.45)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>
              Notes (optional)
            </div>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={3}
              style={{ width: '100%', padding: '8px 10px', borderRadius: 7, border: '1px solid rgba(27,42,74,0.15)', font: '400 14px Inter, sans-serif', resize: 'vertical' }}
            />
          </div>
          {formError && <div className='banner-error' style={{ marginBottom: 12 }}>{formError}</div>}
          <button
            onClick={handleCreate}
            disabled={saving}
            style={{
              background: '#0F7C6C', color: '#fff', border: 'none', borderRadius: 8,
              padding: '9px 18px', font: '600 13px Inter, sans-serif', cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.6 : 1,
            }}
          >
            {saving ? 'Saving…' : 'Save lesson'}
          </button>
        </div>
      )}

      {loading && <div style={{ font: '400 13px Inter, sans-serif', color: 'rgba(27,42,74,0.4)' }}>Loading lessons…</div>}
      {error && <div className='banner-error'>{error}</div>}
      {!loading && !error && lessons.length === 0 && (
        <ComingSoon label='Lesson logging' />
      )}
      {!loading && !error && lessons.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {[...lessons].reverse().map(lesson => (
            <div key={lesson.id} style={{ border: '1px solid rgba(27,42,74,0.08)', borderRadius: 10, padding: '14px 16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                <div>
                  <div style={{ font: '500 14px Inter, sans-serif', color: '#1B2A4A' }}>
                    {formatDateTime(lesson.scheduledAt)}
                    {lesson.durationMinutes != null ? ` · ${lesson.durationMinutes} min` : ''}
                  </div>
                  {(lesson.studentInitial || lesson.instructorInitial) && (
                    <div style={{ font: '400 12px Inter, sans-serif', color: 'rgba(27,42,74,0.45)', marginTop: 2 }}>
                      Signed off — Student: {lesson.studentInitial || '—'} · Instructor: {lesson.instructorInitial || '—'}
                    </div>
                  )}
                  {lesson.notes && (
                    <div style={{ font: '400 13px Inter, sans-serif', color: 'rgba(27,42,74,0.55)', marginTop: 4, whiteSpace: 'pre-wrap' }}>
                      {lesson.notes}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                  {(() => {
                    // finished_at === null covers three different things: a lesson actually
                    // started live (timer running), a future Calendar booking that hasn't
                    // happened yet, and a cancelled booking (cancel_booked_lesson() never
                    // sets finished_at) — only the first is really "in progress"; the other
                    // two fall through to lesson.status, which already reads 'scheduled' or
                    // 'cancelled' correctly.
                    const pending = lesson.status === 'scheduled' && lesson.finishedAt === null
                    const isLive = pending && new Date(lesson.scheduledAt).getTime() <= Date.now()
                    const label = pending ? (isLive ? 'In progress' : 'Scheduled') : lesson.status
                    const color = pending ? (isLive ? '#0F7C6C' : STATUS_COLOR.scheduled) : STATUS_COLOR[lesson.status]
                    return (
                      <span style={{ font: '600 11.5px Inter, sans-serif', color, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        {label}
                      </span>
                    )
                  })()}
                  {!readOnly && lesson.status === 'scheduled' && lesson.finishedAt !== null && (
                    <button
                      onClick={() => handleCancel(lesson.id)}
                      disabled={saving}
                      style={{ background: 'none', border: 'none', font: '500 12px Inter, sans-serif', color: '#C0522A', cursor: saving ? 'default' : 'pointer' }}
                    >
                      Cancel
                    </button>
                  )}
                  <button
                    onClick={() => setExpandedLessonId(id => id === lesson.id ? null : lesson.id)}
                    style={{ background: 'none', border: 'none', font: '500 12px Inter, sans-serif', color: '#0F7C6C', cursor: 'pointer', padding: 0 }}
                  >
                    {expandedLessonId === lesson.id ? 'Hide skills ▲' : 'Assess skills ▼'}
                  </button>
                </div>
              </div>
              {expandedLessonId === lesson.id && <LessonAssessmentPanel lessonId={lesson.id} readOnly={readOnly} />}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// Mark cycle for one CARS Form A cell: blank -> Taught (X) -> Needs improvement (/) ->
// Satisfied (✓) -> blank. A single outcome per (lesson, skill), matching this project's
// established lesson_skill_assessments model (migration 057) exactly — "taught and
// needs improvement" or "taught and satisfied" is one mark, not two independent flags.
const CARS_MARK_CYCLE: SkillOutcome[] = ['not_assessed', 'taught', 'needs_practice', 'demonstrated']
const CARS_MARK_SYMBOL: Record<SkillOutcome, string> = {
  not_assessed: '',
  taught: 'X',
  needs_practice: '/',
  demonstrated: '✓',
}
// Bright, at-a-glance colors so a row of marks reads at a glance — taught/in-progress
// (blue) vs. needs more work (red) vs. satisfied (green) stand apart from each other and
// from the navy body text used everywhere else on the page.
const CARS_MARK_COLOR: Record<SkillOutcome, string> = {
  not_assessed: '#1B2A4A',
  taught: '#2563EB',
  needs_practice: '#DC2626',
  demonstrated: '#16A34A',
}

function toTimeShort(iso: string | null) {
  if (!iso) return '—'
  return new Date(iso).toLocaleTimeString('en-CA', { hour: '2-digit', minute: '2-digit' })
}

const CARS_LABEL_COL_WIDTH = 230
const CARS_LESSON_COL_WIDTH = 108

// One lesson = one column. Laid out with CSS Grid (not an HTML <table>) specifically so
// this component — which owns its own useLessonAssessments(lesson.id) call, same as every
// other per-lesson data scope in this file — can place its cells (one per skill row, plus
// its header and two initial cells) directly into the shared grid by column/row index,
// without needing to be a literal child of a <tr> matching some other component's rows.
function CarsLessonColumn({
  lesson, colIndex, rowNumber, skills, readOnly, updateLesson,
}: {
  lesson: Lesson
  colIndex: number
  rowNumber: number
  skills: DrivingSkill[]
  readOnly: boolean
  updateLesson: ReturnType<typeof useLessons>['updateLesson']
}) {
  const { assessments, saveAssessment } = useLessonAssessments(lesson.id)
  const assessmentBySkill = new Map(assessments.map(a => [a.skillId, a.outcome]))
  const [studentInitial, setStudentInitial] = useState(lesson.studentInitial ?? '')
  const [instructorInitial, setInstructorInitial] = useState(lesson.instructorInitial ?? '')

  useEffect(() => {
    setStudentInitial(lesson.studentInitial ?? '')
    setInstructorInitial(lesson.instructorInitial ?? '')
  }, [lesson.studentInitial, lesson.instructorInitial])

  async function handleCycle(skillId: string) {
    if (readOnly) return
    const current = assessmentBySkill.get(skillId) ?? 'not_assessed'
    const next = CARS_MARK_CYCLE[(CARS_MARK_CYCLE.indexOf(current) + 1) % CARS_MARK_CYCLE.length]
    await saveAssessment(skillId, next, null)
  }

  function commitInitial(field: 'studentInitial' | 'instructorInitial', value: string) {
    if (readOnly) return
    const trimmed = value.trim()
    if (field === 'studentInitial' && trimmed === (lesson.studentInitial ?? '')) return
    if (field === 'instructorInitial' && trimmed === (lesson.instructorInitial ?? '')) return
    updateLesson(lesson.id, { [field]: trimmed || null })
  }

  const cellBase: React.CSSProperties = {
    gridColumn: colIndex, border: '1px solid rgba(27,42,74,0.08)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px 4px',
  }
  const initialInputStyle: React.CSSProperties = {
    width: 48, padding: '5px 6px', borderRadius: 5, border: '1px solid rgba(27,42,74,0.15)',
    font: '500 12.5px Inter, sans-serif', textAlign: 'center', background: readOnly ? 'rgba(27,42,74,0.03)' : '#fff',
  }

  return (
    <>
      <div style={{
        ...cellBase, gridRow: 1, background: 'rgba(27,42,74,0.04)', flexDirection: 'column', gap: 2,
      }}>
        <div style={{ font: '700 13px Inter, sans-serif', color: '#1B2A4A' }}>Lesson {rowNumber}</div>
        <div style={{ font: '400 10.5px Inter, sans-serif', color: 'rgba(27,42,74,0.55)', whiteSpace: 'nowrap' }}>
          {formatDateShort(lesson.scheduledAt)}
        </div>
        <div style={{ font: '400 10.5px Inter, sans-serif', color: 'rgba(27,42,74,0.55)', whiteSpace: 'nowrap' }}>
          {toTimeShort(lesson.scheduledAt)}–{toTimeShort(lesson.finishedAt)}
        </div>
      </div>

      {skills.map((skill, i) => {
        const outcome = assessmentBySkill.get(skill.id) ?? 'not_assessed'
        return (
          <div
            key={skill.id}
            onClick={() => handleCycle(skill.id)}
            style={{
              ...cellBase, gridRow: i + 2,
              font: '700 16px Inter, sans-serif', userSelect: 'none', cursor: readOnly ? 'default' : 'pointer',
              color: CARS_MARK_COLOR[outcome],
              background: i % 2 === 1 ? 'rgba(27,42,74,0.015)' : '#fff',
            }}
          >
            {CARS_MARK_SYMBOL[outcome]}
          </div>
        )
      })}

      <div style={{ ...cellBase, gridRow: skills.length + 2, background: 'rgba(27,42,74,0.02)' }}>
        <input
          value={studentInitial}
          onChange={e => setStudentInitial(e.target.value)}
          onBlur={e => commitInitial('studentInitial', e.target.value)}
          readOnly={readOnly}
          maxLength={6}
          style={initialInputStyle}
        />
      </div>
      <div style={{ ...cellBase, gridRow: skills.length + 3, background: 'rgba(27,42,74,0.02)' }}>
        <input
          value={instructorInitial}
          onChange={e => setInstructorInitial(e.target.value)}
          onBlur={e => commitInitial('instructorInitial', e.target.value)}
          readOnly={readOnly}
          maxLength={6}
          style={initialInputStyle}
        />
      </div>
    </>
  )
}

// Skills are rows (full name, read once, no abbreviation) and lessons are columns
// (identified by number + date, matching the physical CARS Learning Form A exactly) —
// the opposite axis arrangement from an earlier version of this grid, which put skills in
// abbreviated columns and forced instructors to cross-reference a legend to mark anything.
// A CSS Grid, not an HTML <table>, so CarsLessonColumn (one per lesson, each owning its own
// useLessonAssessments call) can place its own cells by row/column index.
function CarsTrackingGrid({
  lessons, lessonsLoading, lessonsError, updateLesson, readOnly,
}: {
  lessons: Lesson[]
  lessonsLoading: boolean
  lessonsError: string | null
  updateLesson: ReturnType<typeof useLessons>['updateLesson']
  readOnly: boolean
}) {
  const { skills, loading: skillsLoading, error: skillsError } = useDrivingSkills()
  const carsSkills = skills.filter(s => s.category === 'CARS Form A')

  if (lessonsLoading || skillsLoading) {
    return <div style={{ font: '400 13px Inter, sans-serif', color: 'rgba(27,42,74,0.4)' }}>Loading tracking sheet…</div>
  }
  if (lessonsError) return <div className='banner-error'>{lessonsError}</div>
  if (skillsError) return <div className='banner-error'>{skillsError}</div>
  if (lessons.length === 0) {
    return (
      <div style={{ font: '400 13px Inter, sans-serif', color: 'rgba(27,42,74,0.45)' }}>
        No in-car lessons logged yet. Start a lesson above to begin tracking it here.
      </div>
    )
  }

  const totalRows = carsSkills.length + 3

  return (
    <div>
      <div style={{ overflowX: 'auto' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: `${CARS_LABEL_COL_WIDTH}px repeat(${lessons.length}, ${CARS_LESSON_COL_WIDTH}px)`,
          gridTemplateRows: `repeat(${totalRows}, min-content)`,
          width: 'max-content',
        }}>
          {/* Corner header */}
          <div style={{
            gridColumn: 1, gridRow: 1, border: '1px solid rgba(27,42,74,0.08)', background: 'rgba(27,42,74,0.04)',
            display: 'flex', alignItems: 'center', padding: '8px 12px',
            font: '700 12px Inter, sans-serif', color: '#1B2A4A',
          }}>
            Evaluation area
          </div>

          {/* Skill name labels, full text, one per row */}
          {carsSkills.map((skill, i) => (
            <div
              key={skill.id}
              style={{
                gridColumn: 1, gridRow: i + 2, border: '1px solid rgba(27,42,74,0.08)',
                display: 'flex', alignItems: 'center', padding: '8px 12px',
                font: '500 13px Inter, sans-serif', color: '#1B2A4A', lineHeight: 1.3,
                background: i % 2 === 1 ? 'rgba(27,42,74,0.015)' : '#fff',
              }}
            >
              {i + 1}. {skill.name}
            </div>
          ))}

          {/* Sign-off row labels */}
          <div style={{
            gridColumn: 1, gridRow: carsSkills.length + 2, border: '1px solid rgba(27,42,74,0.08)',
            display: 'flex', alignItems: 'center', padding: '8px 12px', background: 'rgba(27,42,74,0.02)',
            font: '600 12.5px Inter, sans-serif', color: '#1B2A4A',
          }}>
            Student initial
          </div>
          <div style={{
            gridColumn: 1, gridRow: carsSkills.length + 3, border: '1px solid rgba(27,42,74,0.08)',
            display: 'flex', alignItems: 'center', padding: '8px 12px', background: 'rgba(27,42,74,0.02)',
            font: '600 12.5px Inter, sans-serif', color: '#1B2A4A',
          }}>
            Instructor initial
          </div>

          {lessons.map((lesson, i) => (
            <CarsLessonColumn
              key={lesson.id} lesson={lesson} colIndex={i + 2} rowNumber={i + 1}
              skills={carsSkills} readOnly={readOnly} updateLesson={updateLesson}
            />
          ))}
        </div>
      </div>
      <div style={{ marginTop: 14, display: 'flex', flexWrap: 'wrap', gap: 16, font: '400 12.5px Inter, sans-serif', color: 'rgba(27,42,74,0.55)' }}>
        <span><strong style={{ color: '#2563EB' }}>X</strong> = Taught</span>
        <span><strong style={{ color: '#DC2626' }}>/</strong> = Needs improvement</span>
        <span><strong style={{ color: '#16A34A' }}>&#10003;</strong> = Satisfied</span>
        <span>Click a cell to cycle through marks.</span>
      </div>
    </div>
  )
}

function EndRelationshipAction({ relationshipId, onEnded }: { relationshipId: string; onEnded: () => void }) {
  // Deliberately NOT useStudents() here: that hook fetches the full active-student list
  // (3 queries) as a side effect of being called at all, which this page never displays —
  // every visit to a student's detail page would otherwise fire and immediately discard
  // that fetch. This action only ever needs one RPC call for one relationship.
  const [ending, setEnding] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const ERROR_MESSAGES: Record<string, string> = {
    unauthorized: 'You are not authorized to end this relationship.',
    not_found: 'This student relationship could not be found.',
    relationship_not_active: 'This relationship has already ended.',
    internal_error: 'Something went wrong. Please try again.',
  }

  async function handleConfirm() {
    setError(null)
    setEnding(true)
    const { data, error: rpcError } = await supabase.rpc('end_student_relationship', {
      p_relationship_id: relationshipId,
    })
    setEnding(false)
    const result = rpcError ? { ok: false, code: 'internal_error' } : (data as { ok: boolean; code?: string })
    if (!result.ok) {
      setError(ERROR_MESSAGES[result.code ?? ''] ?? 'Could not end this relationship.')
      setConfirming(false)
      return
    }
    onEnded()
  }

  if (!confirming) {
    return (
      <button
        onClick={() => setConfirming(true)}
        style={{ background: 'none', border: '1px solid rgba(224,102,59,0.3)', borderRadius: 7, padding: '7px 14px', font: '500 12.5px Inter, sans-serif', color: '#C0522A', cursor: 'pointer' }}
      >
        End relationship
      </button>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
      {error && <div className='banner-error' style={{ padding: '6px 10px', font: '400 12px Inter, sans-serif' }}>{error}</div>}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ font: '400 12.5px Inter, sans-serif', color: 'rgba(27,42,74,0.55)' }}>
          End this student relationship?
        </span>
        <button
          onClick={handleConfirm}
          disabled={ending}
          style={{ background: '#C0522A', color: '#fff', border: 'none', borderRadius: 7, padding: '7px 14px', font: '600 12.5px Inter, sans-serif', cursor: ending ? 'default' : 'pointer', opacity: ending ? 0.6 : 1 }}
        >
          {ending ? 'Ending…' : 'Confirm'}
        </button>
        <button
          onClick={() => { setConfirming(false); setError(null) }}
          disabled={ending}
          style={{ background: 'none', border: 'none', font: '500 12.5px Inter, sans-serif', color: 'rgba(27,42,74,0.5)', cursor: 'pointer' }}
        >
          Cancel
        </button>
      </div>
    </div>
  )
}

const ASSIGNMENT_STATUS_COLOR: Record<AssignmentStatus, string> = {
  open: '#9A6C0E',
  completed: '#0F7C6C',
  cancelled: 'rgba(27,42,74,0.4)',
}

function PracticeAssignments({
  relationshipId, readOnly, prefillSkill, onPrefillConsumed,
}: {
  relationshipId: string
  readOnly: boolean
  prefillSkill: { skillId: string; skillName: string } | null
  onPrefillConsumed: () => void
}) {
  const { assignments, loading, error, saving, createAssignment, closeAssignment } = usePracticeAssignments(relationshipId)
  const { skills, loading: skillsLoading } = useDrivingSkills()
  const [formOpen, setFormOpen] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [skillId, setSkillId] = useState('')
  const [instructions, setInstructions] = useState('')
  const [dueDate, setDueDate] = useState('')

  useEffect(() => {
    if (prefillSkill) {
      setFormOpen(true)
      setSkillId(prefillSkill.skillId)
      onPrefillConsumed()
    }
    // onPrefillConsumed is stable (defined inline by the parent per render is fine here
    // since it only clears a single piece of state); re-running this effect whenever
    // prefillSkill itself changes is the actual intent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillSkill])

  const NO_SKILL = '__none__'

  const CREATE_ERROR_MESSAGES: Record<string, string> = {
    instructions_required: 'Instructions are required.',
    instructions_too_long: 'Instructions are too long (2000 characters max).',
    relationship_not_active: 'This student relationship is no longer active, so a new assignment cannot be created.',
    relationship_not_found: 'The student relationship could not be found.',
    unauthorized: 'You are not authorized to assign practice to this student.',
    skill_not_found: 'That skill could not be found.',
    internal_error: 'Something went wrong. Please try again.',
  }

  async function handleCreate() {
    setFormError(null)
    if (!instructions.trim()) { setFormError('Instructions are required.'); return }
    const result = await createAssignment(instructions.trim(), skillId === NO_SKILL || skillId === '' ? null : skillId, dueDate || null)
    if (!result.ok) {
      setFormError(CREATE_ERROR_MESSAGES[result.code ?? ''] ?? 'Could not create the assignment.')
      return
    }
    setFormOpen(false)
    setSkillId('')
    setInstructions('')
    setDueDate('')
  }

  const open = assignments.filter(a => a.status === 'open')
  const history = assignments.filter(a => a.status !== 'open')

  return (
    <div>
      {!readOnly && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
          <button
            onClick={() => { setFormOpen(v => !v); setFormError(null) }}
            style={{
              background: formOpen ? 'rgba(27,42,74,0.06)' : '#0F7C6C', color: formOpen ? '#1B2A4A' : '#fff',
              border: 'none', borderRadius: 8, padding: '9px 16px', font: '600 13px Inter, sans-serif', cursor: 'pointer',
            }}
          >
            {formOpen ? 'Cancel' : '+ Assign practice'}
          </button>
        </div>
      )}

      {!readOnly && formOpen && (
        <div style={{ background: 'rgba(27,42,74,0.03)', border: '1px solid rgba(27,42,74,0.08)', borderRadius: 11, padding: 18, marginBottom: 18 }}>
          <div className='fg2' style={{ marginBottom: 14 }}>
            <div>
              <div style={{ font: '500 11.5px Inter, sans-serif', color: 'rgba(27,42,74,0.45)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>
                Skill (optional)
              </div>
              <select
                value={skillId}
                onChange={e => setSkillId(e.target.value)}
                disabled={skillsLoading}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 7, border: '1px solid rgba(27,42,74,0.15)', font: '400 14px Inter, sans-serif' }}
              >
                <option value={NO_SKILL}>General practice (no specific skill)</option>
                {skills.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <div style={{ font: '500 11.5px Inter, sans-serif', color: 'rgba(27,42,74,0.45)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>
                Due date (optional)
              </div>
              <input
                type='date'
                value={dueDate}
                onChange={e => setDueDate(e.target.value)}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 7, border: '1px solid rgba(27,42,74,0.15)', font: '400 14px Inter, sans-serif' }}
              />
            </div>
          </div>
          <div style={{ marginBottom: 14 }}>
            <div style={{ font: '500 11.5px Inter, sans-serif', color: 'rgba(27,42,74,0.45)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>
              Instructions
            </div>
            <textarea
              value={instructions}
              onChange={e => setInstructions(e.target.value)}
              rows={3}
              placeholder='e.g. Practice parallel parking for 15 minutes, focus on mirror checks before reversing'
              style={{ width: '100%', padding: '8px 10px', borderRadius: 7, border: '1px solid rgba(27,42,74,0.15)', font: '400 14px Inter, sans-serif', resize: 'vertical' }}
            />
          </div>
          {formError && <div className='banner-error' style={{ marginBottom: 12 }}>{formError}</div>}
          <button
            onClick={handleCreate}
            disabled={saving}
            style={{
              background: '#0F7C6C', color: '#fff', border: 'none', borderRadius: 8,
              padding: '9px 18px', font: '600 13px Inter, sans-serif', cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.6 : 1,
            }}
          >
            {saving ? 'Saving…' : 'Save assignment'}
          </button>
        </div>
      )}

      {loading && <div style={{ font: '400 13px Inter, sans-serif', color: 'rgba(27,42,74,0.4)' }}>Loading assignments…</div>}
      {error && <div className='banner-error'>{error}</div>}

      {!loading && !error && assignments.length === 0 && (
        <ComingSoon label='Practice assignments' />
      )}

      {!loading && !error && open.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: history.length > 0 ? 18 : 0 }}>
          {open.map(a => (
            <div key={a.id} style={{ border: '1px solid rgba(27,42,74,0.08)', borderRadius: 10, padding: '14px 16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                <div>
                  {a.skillName && (
                    <div style={{ font: '600 12px Inter, sans-serif', color: '#1B2A4A', marginBottom: 2 }}>{a.skillName}</div>
                  )}
                  <div style={{ font: '400 13px Inter, sans-serif', color: 'rgba(27,42,74,0.65)', whiteSpace: 'pre-wrap' }}>{a.instructions}</div>
                  <div style={{ font: '400 11.5px Inter, sans-serif', color: 'rgba(27,42,74,0.4)', marginTop: 6 }}>
                    Assigned {formatDateShort(a.createdAt)}{a.dueDate && ` · Due ${formatDate(a.dueDate)}`}
                  </div>
                </div>
                {!readOnly && (
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                    <button
                      onClick={() => closeAssignment(a.id, 'completed')}
                      disabled={saving}
                      style={{ background: 'none', border: '1px solid rgba(15,124,108,0.3)', borderRadius: 7, padding: '5px 10px', font: '500 12px Inter, sans-serif', color: '#0F7C6C', cursor: saving ? 'default' : 'pointer' }}
                    >
                      Mark done
                    </button>
                    <button
                      onClick={() => closeAssignment(a.id, 'cancelled')}
                      disabled={saving}
                      style={{ background: 'none', border: 'none', font: '500 12px Inter, sans-serif', color: '#C0522A', cursor: saving ? 'default' : 'pointer' }}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && !error && history.length > 0 && (
        <div>
          <div style={{ font: '600 12px Inter, sans-serif', color: 'rgba(27,42,74,0.4)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>
            History
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {history.map(a => (
              <div key={a.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(27,42,74,0.06)', paddingBottom: 8, gap: 10 }}>
                <div>
                  <div style={{ font: '400 13px Inter, sans-serif', color: 'rgba(27,42,74,0.6)' }}>
                    {a.skillName && <span style={{ fontWeight: 600, color: '#1B2A4A' }}>{a.skillName}: </span>}
                    {a.instructions}
                  </div>
                  <div style={{ font: '400 11px Inter, sans-serif', color: 'rgba(27,42,74,0.4)', marginTop: 2 }}>
                    {a.status === 'completed' && a.completedAt ? `Completed ${formatDateShort(a.completedAt)}` : 'Cancelled'}
                  </div>
                </div>
                <span style={{ font: '600 11px Inter, sans-serif', color: ASSIGNMENT_STATUS_COLOR[a.status], textTransform: 'uppercase', letterSpacing: '0.5px', flexShrink: 0 }}>
                  {a.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function ComingSoon({ label }: { label: string }) {
  return (
    <div style={{
      padding: '20px 22px', borderRadius: 11,
      background: 'rgba(27,42,74,0.03)', border: '1px dashed rgba(27,42,74,0.12)',
      font: '400 13px Inter, sans-serif', color: 'rgba(27,42,74,0.35)', textAlign: 'center',
    }}>
      {label} will appear here in a future phase.
    </div>
  )
}

type TabKey = 'profile' | 'tracking' | 'skills' | 'practice' | 'mock'

const TABS: { key: TabKey; label: string }[] = [
  { key: 'profile',  label: 'Profile' },
  { key: 'tracking', label: 'Lesson Logging & Tracking Sheet' },
  { key: 'skills',   label: 'Skill Progress' },
  { key: 'practice', label: 'Practice Assignments' },
  { key: 'mock',     label: 'Mock Test' },
]

// A pill-style segmented control rather than an underlined scrolling strip: six labels is
// Spans the full available width (an even split per tab) with generous padding and a
// clear underline + color change on the active tab, rather than a tightly clustered pill
// group competing for space on the left — there's a whole page width available and five
// tabs don't need to be crammed together.
function TabBar({ active, onChange }: { active: TabKey; onChange: (k: TabKey) => void }) {
  return (
    <div style={{
      display: 'flex', marginBottom: 24, borderBottom: '2px solid rgba(27,42,74,0.08)',
    }}>
      {TABS.map(t => (
        <button
          key={t.key}
          onClick={() => onChange(t.key)}
          style={{
            flex: 1, padding: '16px 10px', border: 'none', background: 'none',
            borderBottom: active === t.key ? '3px solid #0F7C6C' : '3px solid transparent',
            marginBottom: -2,
            font: active === t.key ? '600 14px/1.3 Inter, sans-serif' : '500 14px/1.3 Inter, sans-serif',
            color: active === t.key ? '#0F7C6C' : 'rgba(27,42,74,0.55)', cursor: 'pointer',
            textAlign: 'center',
          }}
        >
          {t.label}
        </button>
      ))}
    </div>
  )
}

// Guardian contact is required (and shown) only for a minor, mirroring the same
// under-18 rule migration 067's own CHECK constraint enforces at the database layer —
// the product owner's own classification table still puts every under-18 band in
// "Minor," it just distinguishes two sub-bands for messaging purposes. The "not yet
// eligible for a G1" note is purely informational (under-16 specifically); it does not
// change which age requires guardian contact.
function ProfileTab({ student, expiry }: {
  student: StudentData
  expiry: { text: string; color: string }
}) {
  const age = ageInYears(student.dateOfBirth)
  const isMinor = age !== null && age < 18
  const tooYoungForG1 = age !== null && age < 16

  return (
    <>
      <Section title='Contact'>
        <div className='fg2'>
          <Field label='Full name' value={student.fullName ?? '—'} />
          <Field label='Email'     value={student.email} />
          <Field label='Phone'     value={student.phone ?? '—'} />
          <Field label='Sex'       value={student.sex ? student.sex[0].toUpperCase() + student.sex.slice(1) : '—'} />
        </div>
      </Section>

      <Section title='G1 Licence'>
        <div className='fg2'>
          <Field label='Licence number' value={student.g1LicenceNumber ?? '—'} />
          <Field label='Issue date'     value={formatDate(student.g1IssueDate)} />
          <Field label='Date of birth'  value={formatDate(student.dateOfBirth)} />
          <Field label='Expiry date'    value={expiry.text} valueColor={expiry.color} />
        </div>
        {student.g1ExpiryDate === null && (
          <div style={{ marginTop: 14, font: '400 12.5px Inter, sans-serif', color: 'rgba(27,42,74,0.4)' }}>
            G1 details have not been added yet. The student can update these in Settings.
          </div>
        )}
        {tooYoungForG1 && (
          <div className='banner-info' style={{ marginTop: 14 }}>
            Not yet eligible for a G1 licence at this age.
          </div>
        )}
      </Section>

      {isMinor && (
        <Section title='Parent / guardian'>
          <div className='fg2'>
            <Field label='Guardian email' value={student.guardianEmail ?? '—'} />
            <Field label='Guardian phone' value={student.guardianPhone ?? '—'} />
          </div>
          {!student.guardianEmail && !student.guardianPhone && (
            <div style={{ marginTop: 14, font: '400 12.5px Inter, sans-serif', color: 'rgba(27,42,74,0.4)' }}>
              This student is a minor but has no guardian contact on file yet.
            </div>
          )}
        </Section>
      )}
    </>
  )
}

export default function StudentDetail() {
  // Keyed by relationshipId, not studentId: a student can have more than one relationship
  // row with the same instructor over time (one ended + one re-enrolled, migration 059) —
  // loading by studentId alone would be ambiguous about which period to show. The exact
  // relationship row is the single source of truth for this page; studentId/profile data is
  // derived FROM it, never taken independently from the URL.
  const { relationshipId } = useParams<{ relationshipId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()

  const [student, setStudent] = useState<StudentData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error,   setError]   = useState<string | null>(null)
  const [prefillSkill, setPrefillSkill] = useState<{ skillId: string; skillName: string } | null>(null)
  const [activeTab, setActiveTab] = useState<TabKey>('profile')

  // Lifted here (rather than called separately inside LessonLoggingPanel and
  // CarsTrackingGrid) so both halves of the merged tab share one lesson list and
  // one set of mutations — starting or ending a lesson in the logging panel must be
  // immediately visible as a new column in the CARS grid below it, not require a
  // separate, unsynced fetch.
  const lessonsApi = useLessons(student?.relationshipId)

  useEffect(() => {
    if (!user || !relationshipId) return
    const uid = user.id
    const relId = relationshipId

    async function load() {
      setLoading(true)
      setError(null)

      // Load the exact relationship by its own id. RLS "relationships: own read" returns a
      // row only when instructor_id = auth.uid() OR student_id = auth.uid() — a forged or
      // another instructor's relationshipId in the URL simply returns no row (not an error
      // that would distinguish "doesn't exist" from "not yours"), so this cannot be used to
      // access another instructor's student by manipulating the URL.
      const { data: rel, error: relErr } = await supabase
        .from('instructor_student_relationships')
        .select('id, instructor_id, student_id, started_at, ended_at, status, school_id')
        .eq('id', relId)
        .maybeSingle()

      if (relErr || !rel) {
        setError('Student not found.')
        setLoading(false)
        return
      }

      // Defense-in-depth: this route is already gated to instructors only (ProtectedRoute),
      // and a user's primary_role is immutable once set (migrations 051/052), so the
      // student_id branch of "relationships: own read" can never match this caller in
      // practice — but the check costs nothing and documents the invariant explicitly,
      // matching this codebase's existing ownership-check style (e.g. migration 055/059).
      if (rel.instructor_id !== uid) {
        setError('Student not found.')
        setLoading(false)
        return
      }

      // Fetch the student's public profile, using student_id FROM THE VERIFIED RELATIONSHIP
      // ROW — never an independent URL parameter. RLS "profiles: instructor read students"
      // (widened by migration 060 to also cover status = 'ended') grants this for both an
      // active and a former student relationship.
      const { data: profile, error: profErr } = await supabase
        .from('profiles')
        .select('id, full_name, email, phone')
        .eq('id', rel.student_id)
        .single()

      if (profErr || !profile) {
        setError('Could not load student profile.')
        setLoading(false)
        return
      }

      // RLS "student_profiles: instructor read" — same migration-060 widening.
      const { data: sp } = await supabase
        .from('student_profiles')
        .select('date_of_birth, g1_licence_number, g1_issue_date, g1_expiry_date, sex, guardian_email, guardian_phone')
        .eq('user_id', rel.student_id)
        .maybeSingle()

      setStudent({
        id:             profile.id,
        fullName:       profile.full_name,
        email:          profile.email,
        phone:          profile.phone,
        dateOfBirth:    sp?.date_of_birth    ?? null,
        g1LicenceNumber: sp?.g1_licence_number ?? null,
        g1IssueDate:    sp?.g1_issue_date    ?? null,
        g1ExpiryDate:   sp?.g1_expiry_date   ?? null,
        sex:            sp?.sex ?? null,
        guardianEmail:  sp?.guardian_email ?? null,
        guardianPhone:  sp?.guardian_phone ?? null,
        relationshipId: rel.id,
        startedAt:      rel.started_at,
        schoolId:       rel.school_id,
        status:         rel.status,
        endedAt:        rel.ended_at,
      })
      setLoading(false)
    }

    load()
  }, [user, relationshipId])

  if (loading) {
    return (
      <div style={{ font: '400 14px Inter, sans-serif', color: 'rgba(27,42,74,0.4)', padding: '24px 0' }}>
        Loading…
      </div>
    )
  }

  if (error || !student) {
    return (
      <div>
        <button
          onClick={() => navigate('/app/instructor/students')}
          style={{ background: 'none', border: 'none', font: '500 13.5px Inter, sans-serif', color: '#0F7C6C', cursor: 'pointer', padding: 0, marginBottom: 20 }}
        >
          ← Back to students
        </button>
        <div className='banner-error'>{error ?? 'Student not found.'}</div>
      </div>
    )
  }

  const expiry = g1ExpiryStatus(student.g1ExpiryDate)
  const isActive = student.status === 'active'

  return (
    <div style={{ maxWidth: 1100 }}>
      {/* Back nav */}
      <button
        onClick={() => navigate('/app/instructor/students')}
        style={{ background: 'none', border: 'none', font: '500 13.5px Inter, sans-serif', color: '#0F7C6C', cursor: 'pointer', padding: 0, marginBottom: 20 }}
      >
        ← Back to students
      </button>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 24 }}>
        <div>
          <div style={{ font: '600 26px Lora, serif', color: '#1B2A4A', marginBottom: 4 }}>
            {student.fullName ?? 'Student'}
          </div>
          <div style={{ font: '400 14px Inter, sans-serif', color: 'rgba(27,42,74,0.5)' }}>
            Student since {formatDate(student.startedAt)}
          </div>
        </div>
        {isActive ? (
          <EndRelationshipAction
            relationshipId={student.relationshipId}
            onEnded={() => navigate('/app/instructor/students')}
          />
        ) : (
          <div style={{
            padding: '6px 12px', borderRadius: 20, background: 'rgba(27,42,74,0.06)',
            font: '600 12px Inter, sans-serif', color: 'rgba(27,42,74,0.55)', flexShrink: 0,
          }}>
            Relationship ended {formatDate(student.endedAt)}
          </div>
        )}
      </div>

      {!isActive && (
        <div className='banner-info' style={{ marginBottom: 20 }}>
          This relationship has ended. You can still review this student's lesson and skill
          history below, but no new lessons, assessments, or changes can be made.
        </div>
      )}

      <TabBar active={activeTab} onChange={setActiveTab} />

      {activeTab === 'profile' && <ProfileTab student={student} expiry={expiry} />}

      {activeTab === 'tracking' && (
        // Lesson Logging & Tracking Sheet, combined into one tab: Start/End a live lesson,
        // see every lesson logged so far, mark CARS Form A skills per lesson, and the
        // overall A-H competency checklist/signatures/payment ledger below — all one
        // continuous record for this student, not scattered across separate tabs.
        <>
          <PackageUsagePanel relationshipId={student.relationshipId} />
          <Section title='Lesson logging'>
            <LessonLoggingPanel {...lessonsApi} readOnly={!isActive} />
          </Section>
          <Section title='Lesson tracking (CARS Form A)'>
            <CarsTrackingGrid
              lessons={lessonsApi.lessons} lessonsLoading={lessonsApi.loading} lessonsError={lessonsApi.error}
              updateLesson={lessonsApi.updateLesson} readOnly={!isActive}
            />
          </Section>
          <Section title='In-car progress sheet'>
            <ProgressSheetPanel relationshipId={student.relationshipId} readOnly={!isActive} />
          </Section>
        </>
      )}

      {activeTab === 'skills' && (
        <>
          <Section title='Skill progress'>
            <SkillProgress
              studentId={student.id}
              readOnly={!isActive}
              onAssignPractice={(skillId, skillName) => setPrefillSkill({ skillId, skillName })}
            />
          </Section>
          <Section title='Skill practice'>
            <PracticeAssignments
              relationshipId={student.relationshipId}
              readOnly={!isActive}
              prefillSkill={prefillSkill}
              onPrefillConsumed={() => setPrefillSkill(null)}
            />
          </Section>
        </>
      )}

      {activeTab === 'practice' && (
        // What the product owner calls "practice assignments": a lesson is assigned ->
        // the student sees its PDF immediately -> the instructor marks the in-car portion
        // complete -> the student's video unlocks. This is curriculum_lesson_assignments
        // (migration 066), distinct from the skill-instruction feature on the Skill
        // Progress tab above, which is a separate, older mechanism
        // (practice_assignments, migration 062).
        <Section title='Practice assignments'>
          <CurriculumAssignmentPanel studentId={student.id} readOnly={!isActive} />
        </Section>
      )}

      {activeTab === 'mock' && (
        <Section title='Mock test sheets'>
          <ComingSoon label='Mock test sheets' />
        </Section>
      )}
    </div>
  )
}
